import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useDataProvider, useListContext, useNotify } from "react-admin";

import { useSchedulingContext } from "./SchedulingContext";

const SeasonClickContext = createContext(null);

export const useSeasonClickContext = () => useContext(SeasonClickContext) || {};

export const SeasonClickProvider = ({ children }) => {
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [opponent, setOpponent] = useState(null);
  const [slot, setSlot] = useState(null);
  const [loading, setLoading] = useState(false);

  const { refetch, loading: refetching } = useListContext()
  const { availability, setRefreshAvailability, loading: schedulingContextLoading } = useSchedulingContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();

  const selectedDateSlots = useMemo(() => availability?.slots?.filter(slot => slot.date === selectedDate) || [], [availability, selectedDate]);

  const updateMatchup = useCallback((selectedSlot) => {
    if (!opponent || !selectedSlot) return;

    const matchup = opponent.matchups[0];
    if (!matchup) return;

    setLoading(true);

    dataProvider.update('draftGames', {
      id: matchup.id,
      data: {
        date: selectedDate,
        startTime: selectedSlot.startTime,
        endTime: selectedSlot.endTime,
        arenaId: selectedSlot.arenaId,
        timezone: selectedSlot.timezone,
      },
    })
      .then(() => {
        notify('resources.draftGames.notifications.matchup_updated', 'success');
        refetch();
        setRefreshAvailability(true);
        setLoading(false);
        setSelectedTeam(null);
        setSelectedDate(null);
        setOpponent(null);
        setSlot(null);
      }).catch(() => {
        notify('resources.draftGames.notifications.matchup_error', 'error');
        setSlot(null);
        setLoading(false);
      });
  }, [dataProvider, notify, opponent, refetch, selectedDate, setRefreshAvailability]);

  const handleSelectCell = useCallback((team, date) => {
    setOpponent(null);
    setSlot(null);
    setSelectedTeam(prev => (prev === team && selectedDate === date) ? null : team);
    setSelectedDate(prev => (prev === date && selectedTeam === team) ? null : date);
  }, [selectedDate, selectedTeam]);

  const handleSelectOpponent = useCallback((selectedOpponent) => {
    setOpponent((prev) => prev?.opponentId === selectedOpponent.opponentId ? null : selectedOpponent);
  }, []);

  const handleSelectSlot = useCallback((selectedSlot) => {
    setSlot(selectedSlot);

    if (opponent && selectedSlot) {
      updateMatchup(selectedSlot);
    }
  }, [opponent, updateMatchup]);

  const value = useMemo(() => ({
    opponent,
    slot,
    selectedTeam,
    selectedDate,
    selectedDateSlots,
    isRefreshing: loading || schedulingContextLoading || refetching,
    handleSelectOpponent,
    handleSelectSlot,
    handleSelectCell,
  }), [
    opponent,
    slot,
    selectedTeam,
    selectedDate,
    selectedDateSlots,
    loading,
    schedulingContextLoading,
    refetching,
    handleSelectOpponent,
    handleSelectSlot,
    handleSelectCell
  ]);

  return <SeasonClickContext.Provider value={value}>
    {children}
  </SeasonClickContext.Provider>
}
